// Article 4 initial review, part 3 (26 Oct 2026): send the client their questionnaire from the Article 4 Review
// form, and see where it stands. The review can only start once the client has submitted it.
//
// The send is refused by the server until the file is properly open (BM-H339) and goes from the matter's fee
// earner's mailbox, never a typed one. This panel shows what the server said, in its words.

import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle, Copy, Mail, Send } from "lucide-react";
import { API } from "../config/api";
import { formatSendRefusal } from "../api/clientForms";

function day(iso) {
  return (iso || "").slice(0, 10);
}

export default function Article4QuestionnairePanel({ matterRef, defaultEmail = "", defaultName = "" }) {
  const [forms, setForms] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [email, setEmail] = useState(defaultEmail);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/article4/questionnaire/matters/${encodeURIComponent(matterRef)}`);
      setForms(data.forms || []);
      setLoadError("");
    } catch (e) {
      setLoadError("Could not check whether the client has a questionnaire.");
    }
  }, [matterRef]);

  useEffect(() => { if (matterRef) load(); }, [matterRef, load]);

  const send = async () => {
    setBusy(true);
    setError("");
    setCopied(false);
    try {
      const { data } = await axios.post(`${API}/article4/questionnaire/init`, {
        matter_ref: matterRef, client_email: email.trim(), ...(defaultName ? { client_name: defaultName } : {}),
      });
      setSent(data);
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(d && typeof d === "object" && d.message && !d.outstanding ? d.message : formatSendRefusal(d, "Article 4"));
    } finally {
      setBusy(false);
    }
  };

  if (!matterRef) return null;
  const latest = (forms || [])[0] || null;
  const submitted = latest && latest.state === "submitted";

  return (
    <div className="mb-6 rounded-lg border border-white/10 p-3 text-sm" data-testid="a4-questionnaire-panel">
      <div className="flex items-center gap-2 font-medium text-slate-200 mb-1">
        <Mail size={14} /> Client questionnaire
        {latest && (
          <span data-testid="a4-questionnaire-state"
            className={`ml-auto px-2 py-0.5 rounded text-[11px] border ${submitted
              ? "bg-status-green/10 text-status-green border-status-green/30"
              : "bg-status-amber/10 text-status-amber border-status-amber/30"}`}>
            {submitted ? `Submitted ${day(latest.submitted_at)}` : `Sent ${day(latest.created_at)}`}
          </span>
        )}
      </div>
      {loadError && <div className="text-xs text-status-red">{loadError}</div>}
      {forms && !latest && (
        <p className="text-xs text-slate-400 mb-2">
          Not sent yet. The review works from the client's answers, so send this first, once the quote is agreed and
          the file is open.
        </p>
      )}
      {latest && !submitted && (
        <p className="text-xs text-slate-400 mb-2">
          Waiting for the client: {latest.answered} of 12 answered, {latest.uploads} file{latest.uploads === 1 ? "" : "s"} attached.
          {latest.invite_sent === false && " The email did not go; copy the link below or send it again."}
        </p>
      )}
      {submitted && (
        <p className="text-xs text-slate-400 flex items-center gap-1">
          <CheckCircle size={12} className="text-status-green" /> The client has sent their answers. You can start the review.
        </p>
      )}
      {forms && !submitted && (
        <div className="flex gap-2 mt-1">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Client email"
            data-testid="a4-questionnaire-email"
            className="flex-1 bg-white/[0.04] border border-white/10 rounded px-2 py-1 text-slate-200" />
          <button type="button" onClick={send} disabled={busy || !email.trim()} data-testid="a4-questionnaire-send"
            title={latest ? "Send a new link" : "Send the questionnaire"}
            className="inline-flex items-center gap-1 px-3 py-1 rounded bg-indigo-600 text-white disabled:opacity-50">
            <Send size={13} /> {busy ? "Sending..." : latest ? "Send again" : "Send"}
          </button>
        </div>
      )}
      {error && <div className="text-xs text-status-red whitespace-pre-line mt-2" data-testid="a4-questionnaire-error">{error}</div>}
      {sent && (
        <div className="text-xs text-slate-400 mt-2" data-testid="a4-questionnaire-sent">
          {sent.dispatch?.sent
            ? `Emailed from ${sent.fee_earner_email}.`
            : `Created, but the email did not go${sent.dispatch?.error ? ` (${sent.dispatch.error})` : ""}. Send the client this link yourself:`}
          <button type="button" className="ml-2 inline-flex items-center gap-1 text-teal"
            onClick={() => { navigator.clipboard?.writeText(sent.client_link); setCopied(true); }}>
            <Copy size={12} /> {copied ? "Copied" : "Copy link"}
          </button>
        </div>
      )}
    </div>
  );
}
